import _ from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { CarouselItem } from '../formats/ImageBlot';

const MIN_VISIBLE_ITEMS_DISTANCE = 3;

export type ImageItem = {
  id: string;
  image: CarouselItem;
};

type Images = readonly [CarouselItem, CarouselItem, ...CarouselItem[]];

export function cycle<T>(items: readonly T[], length: number, from = 0): T[] {
  const result: T[] = [];
  const count = Math.abs(length);
  const direction = length > 0 ? 'after' : 'before';
  const adjustedFrom = (from + items.length) % items.length;

  let i = 0;
  while (i < count) {
    const index =
      direction === 'before'
        ? (adjustedFrom + items.length - (i % items.length) - 1) % items.length
        : (adjustedFrom + i + 1) % items.length;

    const item = items[index];

    if (!item) {
      break;
    }

    if (direction === 'before') {
      result.unshift(item);
    } else {
      result.push(item);
    }

    i += 1;
  }

  return result;
}

type CarouselApi = {
  dispatch: React.Dispatch<Action>;
  addImages: (files: File[], index: number) => void;
  currentIndex: number;
  length: number;
  items: ImageItem[];
};

const CarouselContext = React.createContext<CarouselApi | null>(null);

export function useCarousel() {
  const context = useContext(CarouselContext);
  if (!context) throw new Error('useCarousel must be used inside Carousel');
  return context;
}

function areCarouselItemsEqual(a: CarouselItem, b: CarouselItem) {
  return a === b || a.source === b.source;
}

const defaultIdGenerator = () => _.uniqueId('item-');

function getOrCreateCenteredItem({
  items,
  images,
  generateId,
}: {
  items: ImageItem[];
  images: Images;
  generateId: () => string;
}): ImageItem {
  const centeredItem = items[Math.floor(items.length / 2)];
  const existingImage = centeredItem
    ? images.find((image) => areCarouselItemsEqual(image, centeredItem.image))
    : null;

  if (existingImage == null || centeredItem == null) {
    return {
      id: generateId(),
      image: images[0],
    };
  }
  return centeredItem;
}

export function updateItemsWithNewImages({
  images,
  items,
  generateId = defaultIdGenerator,
}: {
  images: Images;
  items: ImageItem[];
  generateId?: () => string;
}): ImageItem[] {
  const halfIndex = Math.floor(items.length / 2);
  const centeredItem = getOrCreateCenteredItem({ items, images, generateId });
  const imageIndex = images.findIndex((image) =>
    areCarouselItemsEqual(image, centeredItem.image),
  );

  const maxDistanceToEnd = Math.max(
    images.length - (imageIndex + 1),
    imageIndex + 1,
  );
  const visibleItemsDistance = Math.max(
    MIN_VISIBLE_ITEMS_DISTANCE,
    maxDistanceToEnd,
  );

  const before = cycle(images, -visibleItemsDistance, imageIndex);
  const itemsBefore = items.slice(0, halfIndex).reverse();
  const nextItemsBefore: ImageItem[] = [];

  for (let i = before.length - 1; i >= 0; i -= 1) {
    const image = before[i];

    if (image) {
      const existingIndex = itemsBefore.findIndex(
        (item) =>
          areCarouselItemsEqual(item.image, image) &&
          !nextItemsBefore.includes(item),
      );

      const existingItem = itemsBefore[existingIndex];

      if (existingItem && !nextItemsBefore.includes(existingItem)) {
        existingItem.image = image;
        nextItemsBefore.unshift(existingItem);
      } else {
        nextItemsBefore.unshift({
          id: generateId(),
          image,
        });
      }
    }
  }

  const after = cycle(images, visibleItemsDistance, imageIndex);
  const itemsAfter = items.slice(halfIndex + 1);
  const nextItemsAfter: ImageItem[] = [];

  for (let i = 0; i < after.length; i += 1) {
    const image = after[i];

    if (image) {
      const existingIndex = itemsAfter.findIndex(
        (item) =>
          areCarouselItemsEqual(item.image, image) &&
          !nextItemsAfter.includes(item),
      );

      const existingItem = itemsAfter[existingIndex];

      if (existingItem && !nextItemsAfter.includes(existingItem)) {
        existingItem.image = image;
        nextItemsAfter.push(existingItem);
      } else {
        nextItemsAfter.push({
          id: generateId(),
          image,
        });
      }
    }
  }

  const resultItems = [...nextItemsBefore, centeredItem, ...nextItemsAfter];

  const firstUploadPayload = images.find(
    (image) => image.uploadPayload != null,
  )?.uploadPayload;

  const firstUploadingItemIndex = firstUploadPayload
    ? resultItems.findIndex(
        (item) => item.image.uploadPayload === firstUploadPayload,
      )
    : -1;

  if (firstUploadingItemIndex !== -1) {
    const balancedItems = balanceItemsByIndex({
      items: resultItems,
      images,
      index: firstUploadingItemIndex,
      generateId,
    });

    return balancedItems;
  }

  return resultItems;
}

export function balanceItemsByIndex({
  items,
  images,
  index,
  generateId = defaultIdGenerator,
}: {
  items: ImageItem[];
  images: Images;
  index: number;
  generateId?: () => string;
}) {
  const centerIndex = Math.floor(items.length / 2);
  if (index === centerIndex) return items;

  if (index < centerIndex) {
    const firstImage = _.first(items)?.image;
    if (!firstImage) return items;
    const firstImageIndex = images.indexOf(firstImage);
    const newItems = cycle(images, -(centerIndex - index), firstImageIndex);

    return [
      ...newItems.map((image) => ({
        id: generateId(),
        image,
      })),
      ...items,
    ].slice(0, -newItems.length);
  }

  const lastImage = _.last(items)?.image;
  if (!lastImage) return items;
  const lastImageIndex = images.indexOf(lastImage);

  const newItems = cycle(images, index - centerIndex, lastImageIndex);

  return [
    ...items,
    ...newItems.map((image) => ({
      id: generateId(),
      image,
    })),
  ].slice(newItems.length);
}

export function step({
  items,
  images,
  by,
  generateId = defaultIdGenerator,
}: {
  items: ImageItem[];
  images: Images;
  by: number;
  generateId?: () => string;
}) {
  return balanceItemsByIndex({
    items,
    images,
    index: Math.floor(items.length / 2) + by,
    generateId,
  });
}

type State = {
  items: ImageItem[];
  images: Images;
};

type StepAction = {
  type: 'step';
  by: number;
};

type UpdateImagesAction = {
  type: 'updateImages';
  images: Images;
};

type Action = StepAction | UpdateImagesAction;

interface CarouselProps {
  images: Images;
  children: React.ReactNode;
  onAddImages?(files: File[], index: number): void;
}

const Carousel = ({
  images: initialImages,
  children,
  onAddImages,
}: CarouselProps) => {
  const onAddImagesRef = useRef(onAddImages);

  useLayoutEffect(() => {
    onAddImagesRef.current = onAddImages;
  }, [onAddImages]);

  const reducer = useCallback((state: State, action: Action): State => {
    switch (action.type) {
      case 'step': {
        const items = step({
          items: state.items,
          images: state.images,
          by: action.by,
        });

        return {
          ...state,
          items,
        };
      }
      case 'updateImages': {
        const items = updateItemsWithNewImages({
          images: action.images,
          items: state.items,
        });

        return {
          ...state,
          images: action.images,
          items,
        };
      }
      default:
        return state;
    }
  }, []);

  const [{ items, images }, dispatch] = useReducer(
    reducer,
    {
      items: [],
      images: initialImages,
    } as State,
    (init: State): State => {
      const nextItems = updateItemsWithNewImages({
        images: init.images,
        items: init.items,
      });

      return {
        ...init,
        items: nextItems,
      };
    },
  );

  useEffect(() => {
    // we don't considercaption changes as a relevant update for the carousel
    // therefore we only focus on the sources and uploadPayload
    const currentImageSources = images.map(({ source, uploadPayload }) => ({
      source,
      uploadPayload,
    }));
    const initialImageSources = initialImages.map(
      ({ source, uploadPayload }) => ({
        source,
        uploadPayload,
      }),
    );

    if (!_.isEqual(currentImageSources, initialImageSources)) {
      dispatch({ type: 'updateImages', images: initialImages });
    }
  }, [initialImages, images]);

  const { length } = images;

  const addImages = useCallback(
    (files: File[], index: number) => {
      if (onAddImagesRef.current == null) return;
      const halfIndex = Math.floor(items.length / 2);
      const itemAtIndex = items.at(index);
      const originalIndex = itemAtIndex
        ? images.indexOf(itemAtIndex.image)
        : -1;
      const isLast = originalIndex === images.length - 1;

      if (index === -1 || (index === halfIndex - 1 && isLast)) {
        onAddImagesRef.current(files, 0);
      } else {
        onAddImagesRef.current(files, originalIndex + 1);
      }
    },
    [items, images],
  );

  const context = useMemo(() => {
    const selectedItem = items.at(Math.floor(items.length / 2));
    const currentIndex = selectedItem
      ? Math.max(
          images.findIndex((image) => image === selectedItem.image),
          0,
        )
      : 0;

    return {
      dispatch,
      currentIndex,
      length,
      items,
      addImages,
    };
  }, [items, images, length, addImages]);

  return (
    <CarouselContext.Provider value={context}>
      {children}
    </CarouselContext.Provider>
  );
};

export default Carousel;
